import { useCallback, useMemo, useState } from 'react';
import { useEffectOnce } from 'react-use';
import arrowRight from '~/assets/icons/arrow_right.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage } from '~/rage';
import { Currency } from '~/shared';
import {
	setModalBuyDonateItem,
	setModalBuyType,
	setModalBuyVisible,
	useLayoutDispatch,
} from '../../redux';
import type { IModalBuyDonateItem } from '../../redux/slices/shopSlice';
import { formatTime } from '../../utils';
import {
	StyledBackNumbers,
	StyledBottomContainer,
	StyledButtonContent,
	StyledContainer,
	StyledDiscountContainer,
	StyledDiscountTimerContainer,
	StyledGreenShadow,
	StyledMoneyImage,
	StyledMoneyImageShadow,
	StyledMoreBlock,
	StyledOldPrice,
	StyledPrice,
	StyledPriceContainer,
	StyledShadow,
	StyledTag,
	StyledTagsContainer,
} from './ShopDonateItem.styled';

const ShopDonateItem = ({
	name,
	amount,
	price,
	oldPrice,
	discount,
	dateDiscountEnd,
	tags: tagIds,
	donateItemPayload,
}: {
	name?: string;
	amount: number;
	price: number;
	oldPrice?: number;
	discount?: number;
	dateDiscountEnd?: number;
	tags?: string[];
	donateItemPayload?: IModalBuyDonateItem;
}) => {
	const amountString = useMemo(() => amount.toLocaleString('ru-RU'), [amount]);
	const [discountTime, setDiscountTime] = useState('');
	const rubleIcon = useWalletIcon(Currency.Ruble, true);
	const donateIcon = useWalletIcon(Currency.Donate, true);
	const dispatch = useLayoutDispatch();

	const tagsConfig = useMemo(
		() =>
			tagIds
				?.map((id) => rage.donate.getCurrencyShopTagById(id))
				.filter(Boolean) ?? [],
		[tagIds],
	);

	const countTime = useCallback(() => {
		if (dateDiscountEnd) {
			const { time, unit } = formatTime(dateDiscountEnd);
			if (!unit) {
				setDiscountTime(time);
			} else {
				setDiscountTime(`${time} ${unit}`);
			}
		}
	}, [dateDiscountEnd]);

	useEffectOnce(() => {
		countTime();
		const interval = setInterval(countTime, 1000);
		return () => clearInterval(interval);
	});

	const handleClick = useCallback(() => {
		if (donateItemPayload) {
			dispatch(setModalBuyDonateItem(donateItemPayload));
			dispatch(setModalBuyType('donate'));
			dispatch(setModalBuyVisible(true));
		}
	}, [donateItemPayload]);

	return (
		<StyledContainer onClick={handleClick}>
			<StyledBackNumbers>
				{amountString}
				<br />
				{amountString}&nbsp;{amountString}
				<br />
				{amountString}&nbsp;{amountString}&nbsp;
				{amountString.slice(0, amountString.length - 3)}
			</StyledBackNumbers>
			<StyledShadow />
			<StyledGreenShadow />
			<StyledMoneyImageShadow />
			<StyledMoneyImage />
			<StyledTagsContainer>
				{discountTime && discount ? (
					<StyledDiscountTimerContainer>
						<Text>{discount * 100}%</Text>
						<div className={'divider'} />
						<Text>{discountTime}</Text>
					</StyledDiscountTimerContainer>
				) : (
					<>
						{discount ? (
							<StyledDiscountContainer>
								<Text>{discount * 100}%</Text>
							</StyledDiscountContainer>
						) : null}
					</>
				)}
				{tagsConfig?.map((tag) => (
					<StyledTag
						$bgColorNormal={tag?.bgColorNormal ?? ''}
						$textColorNormal={tag?.textColorNormal ?? ''}
						key={tag?.id}
					>
						<Text>{tag?.text}</Text>
					</StyledTag>
				))}
			</StyledTagsContainer>
			<StyledBottomContainer>
				<Text>{name ?? `Пакет «${amountString}»`}</Text>
				<Button
					color={ButtonColors.Buy}
					height={ButtonHeight.Height40}
					style={{ pointerEvents: 'none', width: 'fit-content' }}
				>
					<StyledButtonContent>
						<CurrencyIcon icon={rubleIcon} size="sm" />
						<Text>{amountString}</Text>
					</StyledButtonContent>
				</Button>
				<StyledPriceContainer>
					<StyledPrice $isDonate>
						<CurrencyIcon icon={donateIcon} size="md" />
						<Text>{price.toLocaleString('ru-RU')}</Text>
					</StyledPrice>
					{oldPrice && (
						<StyledOldPrice>
							<CurrencyIcon icon={donateIcon} size={10} />
							<Text>{oldPrice.toLocaleString('ru-RU')}</Text>
							<div className={'line'} />
						</StyledOldPrice>
					)}
				</StyledPriceContainer>
				<StyledMoreBlock>
					<Text>Подробнее</Text>
					<img src={arrowRight} />
				</StyledMoreBlock>
			</StyledBottomContainer>
		</StyledContainer>
	);
};

export { ShopDonateItem };
